import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

const PlateauTechnique = () => {
  const { t, lang } = useLanguage();

  const features = lang === "fr" ? [
    { icon: "fa-microscope", title: "Microscope opératoire", text: "Microchirurgie de précision pour les réparations nerveuses et vasculaires." },
    { icon: "fa-wave-square", title: "Amplificateur de brillance", text: "Imagerie per-opératoire pour un geste chirurgical parfaitement contrôlé." },
    { icon: "fa-droplet", title: "Arthroscopie 4K", text: "Colonne vidéo haute définition pour chirurgie mini-invasive de l'épaule, du coude, du poignet." },
    { icon: "fa-shield-halved", title: "Bloc aseptique classe ISO 5", text: "Flux laminaire et protocoles stricts pour minimiser le risque infectieux." },
  ] : [
    { icon: "fa-microscope", title: "Operating microscope", text: "Precision microsurgery for nerve and vascular repairs." },
    { icon: "fa-wave-square", title: "Fluoroscopy C-arm", text: "Intraoperative imaging for perfectly-controlled surgical gestures." },
    { icon: "fa-droplet", title: "4K arthroscopy", text: "High-definition video tower for minimally-invasive shoulder, elbow, wrist surgery." },
    { icon: "fa-shield-halved", title: "ISO 5 aseptic theatre", text: "Laminar airflow and strict protocols to minimize infection risk." },
  ];

  return (
    <section
      id="plateau-technique"
      data-testid="plateau-technique-section"
      className="section-padding bg-[#F8FAFC]"
    >
      <div className="container-page">
        <div className="max-w-3xl">
          <span className="chip mb-5">
            {lang === "fr" ? "Plateau technique" : "Surgical facilities"}
          </span>
          <h2
            data-testid="plateau-title"
            className="font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]"
          >
            {lang === "fr"
              ? "Un environnement chirurgical à la hauteur de vos attentes"
              : "A surgical environment that meets your expectations"}
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
            {lang === "fr"
              ? "Les interventions sont réalisées au sein de blocs opératoires modernes, équipés des dernières technologies d'imagerie per-opératoire, d'arthroscopie 4K et de microchirurgie. Chaque geste est planifié et exécuté avec la plus grande précision."
              : "Surgery is performed in modern operating theatres, equipped with the latest intraoperative imaging, 4K arthroscopy and microsurgery technologies. Every gesture is planned and executed with the highest precision."}
          </p>
        </div>

        {/* Large image + stacked small images layout */}
        <div className="mt-12 grid lg:grid-cols-12 gap-5">
          {/* Big image */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[16/11] lg:aspect-[4/3.1] rounded-2xl overflow-hidden border border-border group bg-[#0A192F]">
              <img
                src={SITE.blocImages[0]}
                alt={lang === "fr" ? "Bloc opératoire moderne" : "Modern operating room"}
                className="absolute inset-0 h-full w-full object-contain lg:object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
                width="1600"
                height="1200"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent p-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70">
                  {lang === "fr" ? "Bloc opératoire principal" : "Main operating theatre"}
                </p>
                <p className="font-display text-white text-xl font-bold mt-1">
                  Clinique Juge · Marseille
                </p>
              </div>
            </div>
          </div>

          {/* Two small images stacked */}
          <div className="lg:col-span-5 grid grid-cols-2 lg:grid-cols-1 gap-5">
            <div className="relative aspect-[4/3] lg:aspect-[16/9] rounded-2xl overflow-hidden border border-border group bg-[#0A192F]">
              <img
                src={SITE.blocImages[1]}
                alt={lang === "fr" ? "Microchirurgie en cours" : "Ongoing microsurgery"}
                className="absolute inset-0 h-full w-full object-contain lg:object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
                width="1200"
                height="675"
              />
            </div>
            <div className="relative aspect-[4/3] lg:aspect-[16/9] rounded-2xl overflow-hidden border border-border group bg-[#0A192F]">
              <img
                src={SITE.blocImages[2]}
                alt={lang === "fr" ? "Geste chirurgical précis" : "Precise surgical gesture"}
                className="absolute inset-0 h-full w-full object-contain lg:object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
                width="1200"
                height="675"
              />
            </div>
          </div>
        </div>

        {/* Technical features grid */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <div
              key={i}
              data-testid={`plateau-feature-${i}`}
              className="rounded-xl border border-border bg-white p-5 card-hover"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0A192F] text-[#FF5A1F]">
                <i className={`fa-solid ${f.icon}`} aria-hidden="true"></i>
              </div>
              <h3 className="mt-4 font-display font-bold text-navy text-base">{f.title}</h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlateauTechnique;
